import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Check,
  Copy01,
  DotsVertical,
  RefreshCw01,
  Share01,
  ThumbsDown,
  ThumbsUp,
} from '@untitledui/icons';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkBreaks from 'remark-breaks';
import { toast } from 'sonner';

import { InlineProposalPanel } from '@/components/proposals/InlineProposalPanel';
import { BuilderChatHeader } from '@/components/reasoning/BuilderChatHeader';
import { BuilderRecordPanelDesktop } from '@/components/reasoning/BuilderRecordPanel';
import { BuilderRecordPanelMobileOverlay } from '@/components/reasoning/BuilderRecordPanel';
import { BuilderReviewBar } from '@/components/reasoning/BuilderReviewBar';
import { ConversationModals } from '@/components/reasoning/ConversationModals';
import type { ReasoningStepData } from '@/components/reasoning/ReasoningStepsList';
import { useSlideoutWidth } from '@/contexts/SlideoutWidthContext';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import {
  INLINE_PANEL_MAX_WIDTH,
  INLINE_PANEL_MIN_WIDTH,
  useCanHostInlinePanel,
} from '@/hooks/useCanHostInlinePanel';
import { useUserProfileData } from '@/hooks/useUserProfileData';
import { Button } from '@/lib/base/buttons/button';
import { EdgeResizeHandle } from '@/lib/base/resize-handle/edge-resize-handle';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { useSlideoutStackContextOptional } from '@/lib/crm/contexts/SlideoutStackContext';
import {
  ENTITY_BUILDER_AGENT_TYPE,
  reconcileBuilderRoute,
  useBuilderMode,
} from '@/lib/crm/hooks/useBuilderMode';
import { useBuilderSubject } from '@/lib/crm/hooks/useBuilderSubject';
import { getTypeLabelByName } from '@/lib/crm/utils/getTypeLabel';
import { fetchWithDefaults } from '@/lib/http/client';
import { preprocessEntityTags } from '@/lib/markdown/entityRendering';
import { ChainResultsPreview } from '@/lib/proposals/ChainResultsPreview';
import { proposalQueryKeys } from '@/lib/proposals/query/keys';
import { getBuilderPlaceholder } from '@/lib/reasoning/builderPlaceholder';
import { findActiveProcessingTask } from '@/lib/reasoning/chainStatus';
import { conversationService } from '@/lib/reasoning/conversation';
import { ConversationInput } from '@/lib/reasoning/ConversationInput';
import { useChainFeedbackModal } from '@/lib/reasoning/hooks/useChainFeedbackModal';
import { useChatAutoScroll } from '@/lib/reasoning/hooks/useChatAutoScroll';
import { useProposalPaneResize } from '@/lib/reasoning/hooks/useProposalPaneResize';
import {
  SIDEBAR_MAX_WIDTH_PERCENT,
  SIDEBAR_MIN_WIDTH_PERCENT,
  useReasoningSidebarPanel,
} from '@/lib/reasoning/hooks/useReasoningSidebarPanel';
import {
  setConversationHidden,
  setConversationVisible,
  trackChatRun,
} from '@/lib/reasoning/realtime/chatRunTracker';
import type {
  ChainCompletedEvent,
  ChainFailedEvent,
  StepProgressEvent,
  TitleUpdatedEvent,
} from '@/lib/reasoning/realtime/types';
import { useChatProgress } from '@/lib/reasoning/realtime/useChatProgress';
import { useViewHealthMarker } from '@/lib/view-health';
import type { ViewDefinition } from '@/types/dynamic_views';
import { cx } from '@/utils/cx';

import type { UploadedFile } from '../../components/files';
import { InlineFileCard } from '../../components/files';
import { InlineProposal } from '../../components/proposals/InlineProposal';
import { DynamicViewSidebar } from '../../components/reasoning/DynamicViewSidebar';
import { HeroChatInput } from '../../components/reasoning/HeroChatInput';
import { InlineReportChart } from '../../components/reasoning/InlineReportChart';
import { MaterialsList } from '../../components/reasoning/MaterialsList';
import { ReasoningLimitBanner } from '../../components/reasoning/ReasoningLimitBanner';
import { ReasoningSidebar } from '../../components/reasoning/ReasoningSidebar';
import { StepLimitWarningBanner } from '../../components/reasoning/StepLimitWarningBanner';
import { ThinkingIndicator } from '../../components/reasoning/ThinkingIndicator';
import { useProposalSidebar } from '../../contexts/ActionSidebarContext';
import { InlineEditProvider } from '../../contexts/InlineEditContext';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useTitle } from '../../hooks/useTitle';
import { copyToClipboard } from '../../lib/utils/copyToClipboard';
import { useAutoOpenLatestProposal } from './hooks/useAutoOpenLatestProposal';
import {
  EntityMentionSpan,
  MarkdownCopyableTable,
} from './reasoningMarkdownComponents';
import { MessageContent, TypewriterTitle } from './reasoningMessageHelpers';
import { sanitizeSchema } from './reasoningSanitizeSchema';
import { TableScrollProvider } from './TableScrollContext';

interface ReasoningStep {
  id: string;
  step_number: number;
  stage: string;
  content: string;
  status: string;
  is_final: boolean;
  tokens_used?: number;
  artifacts?: Array<{
    id: number;
    title: string;
    artifact_type: { name: string };
    status: string;
    version_number: number;
    parent_artifact?: number;
    is_current_version?: boolean;
    associated_action_id?: number | null;
    associated_action?: unknown;
  }>;
  dynamic_views?: Array<{
    id: number;
    title: string;
    view_type: { name: string; description?: string };
    status: string;
    version_number: number;
    created_at: string;
  }>;
  created_proposals?: Array<{
    id: number;
    title: string;
    resolution: string;
    action_count?: number;
  }>;
  // Raw tool-call results stored on the step. Shape mirrors ToolResult in
  // ReasoningStepsList.tsx — `result` is `unknown` because each tool decides
  // its own return shape.
  tool_results?: Array<{
    tool_name: string;
    tool_input: Record<string, unknown>;
    result: unknown;
    error?: string;
    status?: 'running' | 'done' | 'error';
  }>;
}

interface Chain {
  id: string;
  user_message: string;
  status: string;
  steps: ReasoningStep[];
  files?: UploadedFile[];
  task_id?: string;
  processing_status?: string;
  step_limit_reached?: boolean;
  budget_exceeded?: boolean;
}

interface Conversation {
  id: string;
  title: string;
  initial_prompt: string;
  state: string;
  created_by: number;
  /**
   * What kind of conversation this is. `entity_builder` marks a builder
   * conversation — the authority the route reconciles against, so the mode
   * survives arriving from the conversation list, a bookmark or a shared link.
   */
  agent_type?: string;
  /** The entity a builder conversation is building. */
  builder_entity_type?: string | null;
  /** Null until the agent's first proposal mints the id. */
  builder_entity_id?: string | null;
}

interface ConversationListItem {
  id: string;
  title: string;
  initial_prompt: string;
  state: string;
  created_at: string;
}

// Compact signature of a step's tool_results that captures per-tool status
// transitions (running -> done/error). The poll's signature-diff keys on
// tool_results LENGTH, which stays constant while statuses flip, so we also
// fold in each tool's status plus whether it has a result/error. Missing status
// is normalized to '' so legacy data produces a stable signature.
function toolStatusSignature(
  toolResults?: ReasoningStepData['tool_results']
): string {
  if (!toolResults || toolResults.length === 0) {
    return '';
  }
  return toolResults
    .map(tr => {
      const status = tr.status ?? '';
      const hasResult =
        tr.result !== null && tr.result !== undefined ? 'r' : '';
      const hasError = tr.error ? 'e' : '';
      return `${status}${hasResult}${hasError}`;
    })
    .join('|');
}

// loadConversation retry policy: transient failures (network errors, 5xx,
// 429) get one automatic retry before surfacing anything to the user. Other
// 4xx responses are permanent — retrying cannot change the outcome.
const LOAD_CONVERSATION_MAX_ATTEMPTS = 2;
const LOAD_CONVERSATION_RETRY_DELAY_MS = 1000;

/** Follow-up composer placeholder outside builder mode. */
const DEFAULT_FOLLOW_UP_PLACEHOLDER = 'What can I help with today?';

// Docked-proposal eligibility (`useCanHostInlinePanel`, shared with
// ActionDetailPage): the chat column must keep at least this much width
// beside the nav sidebar and the pane, or the claim is dropped and the
// global overlay renders instead.
const MIN_CHAT_WIDTH_FOR_DOCKED_PROPOSAL = 560;

const wait = (ms: number) =>
  new Promise<void>(resolve => setTimeout(resolve, ms));

/**
 * The most recent proposal in a conversation, by the chronology builder mode
 * uses (chains and steps are ordered; the last listed occurrence is the most
 * recent). `pendingOnly` narrows the scan to proposals that are still OPEN —
 * the two consumers below deliberately differ on this (see their comments).
 */
function lastCreatedProposalId(
  chains: Chain[],
  pendingOnly: boolean
): number | undefined {
  let last: number | undefined;
  for (const chain of chains) {
    for (const step of chain.steps || []) {
      for (const proposal of step.created_proposals || []) {
        if (!pendingOnly || proposal.resolution === 'pending') {
          last = proposal.id;
        }
      }
    }
  }
  return last;
}

export default function ReasoningChat() {
  useTitle('Conversations');

  const { orgId, conversationId } = useParams<{
    orgId?: string;
    conversationId?: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Memoized markdown component overrides for ReasoningChat.
  // Component references are module-scope constants — no closures, empty dep array is intentional.
  // orgId is read inside EntityMentionSpan via useParams().
  const markdownComponents = useMemo(
    () => ({
      span: EntityMentionSpan,
      table: MarkdownCopyableTable,
    }),
    []
  );

  // Add ref for scrollable container
  const chatContainerRef = useRef<HTMLDivElement>(null);
  // Add ref for last user message
  const lastUserMessageRef = useRef<HTMLDivElement>(null);

  const [, setConversations] = useState<ConversationListItem[]>([]);
  const [currentConversation, setCurrentConversation] =
    useState<Conversation | null>(null);
  const [chains, setChains] = useState<Chain[]>([]);
  const [followUpMessage, setFollowUpMessage] = useState('');
  const [optimisticMessage, setOptimisticMessage] = useState<string | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setCollapsedChains] = useState<Set<string>>(new Set());
  const [, setCollapsedSteps] = useState<Set<string>>(new Set());
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [pollingConversationId, setPollingConversationId] = useState<
    string | null
  >(null);
  const [progressStatus, setProgressStatus] = useState<string>('');
  const [partialSteps, setPartialSteps] = useState<ReasoningStepData[]>([]);
  // Dedicated polling-failure flag used by the `polling-ready` view-health
  // marker. Gating on the global `error` state would be too broad — unrelated
  // failures (e.g. a failed regenerate/follow-up request) would suppress the
  // marker during the initial load window and produce false timeout beacons.
  const [pollingFailed, setPollingFailed] = useState(false);

  useViewHealthMarker('conversation-data', {
    ready:
      currentConversation !== null &&
      String(currentConversation.id) === String(conversationId),
  });

  // Register this conversation as visible while mounted so the chat run
  // tracker suppresses its own completion toast (the user is already looking
  // at the conversation). Guarded because the no-conversation home routes
  // also render ReasoningChat with an undefined conversationId.
  useLayoutEffect(() => {
    if (!conversationId) {
      return;
    }
    setConversationVisible(conversationId);
    return () => {
      setConversationHidden(conversationId);
    };
  }, [conversationId]);

  // Refs for stable callbacks (avoid stale closures in useCallback)
  const currentTaskIdRef = useRef<string | null>(null);
  const processedTaskIdRef = useRef<string | null>(null);
  // Conversation id currently rendered on screen. Lets loadConversation
  // distinguish an initial load or post-navigation reload (nothing visible —
  // a failure must show the blocking banner) from a background refresh of
  // content the user is already reading (a failure should not blank that
  // out with a banner). Mirrors `currentConversation` so the check is tied
  // to what the user actually sees, not just what was last fetched.
  const renderedConversationIdRef = useRef<string | null>(null);
  // The conversationId the previous render was showing. Lets Effect 1 detect a
  // genuine switch to a different conversation (vs. a follow-up in the same one,
  // which never changes conversationId) so it can drop the outgoing
  // conversation's task before adopting the destination's.
  const previousConversationIdRef = useRef<string | null>(null);
  // Monotonic counter identifying the most recent loadConversation invocation.
  // A rapid A → B → A switch fires three loads; only the latest may install its
  // result. Each call captures its generation at entry and the success path
  // bails once a newer load has superseded it. The conversation-id route check
  // alone cannot catch A → B → A: the stale A response's id matches the route
  // again, so it would pass an id-only gate and overwrite the newer A load's
  // chains/task state. (The failure path guards on the route, not the
  // generation — see the note there — so overlapping same-conversation refresh
  // failures still surface their toast.)
  const loadGenerationRef = useRef(0);
  // Generation of the most recent loadConversation that SUCCEEDED (rendered
  // content). Lets the failure path suppress a stale failure only when a
  // strictly-newer load has already succeeded — see loadConversation.
  const lastSucceededLoadGenerationRef = useRef(0);

  useEffect(() => {
    renderedConversationIdRef.current = currentConversation
      ? String(currentConversation.id)
      : null;
  }, [currentConversation]);

  // Keep currentTaskIdRef in sync with state
  useEffect(() => {
    currentTaskIdRef.current = currentTaskId;
  }, [currentTaskId]);

  // User profile data
  const { data: profileData } = useUserProfileData();
  const userProfile = profileData?.user;

  // File upload state
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 768
  );
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Materials state (combines proposals and dynamic views)
  const [activeTab, setActiveTab] = useState<'conversation' | 'materials'>(
    'conversation'
  );

  // Share modal state
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const handleCloseShareModal = useCallback(() => {
    setIsShareModalOpen(false);
  }, []);
  const handleOpenShareModal = useCallback(() => {
    setIsShareModalOpen(true);
  }, []);

  // Chat header / mobile menu handlers
  const handleNavigateBack = useCallback(() => {
    navigate(`/${orgId}/conversations`);
  }, [navigate, orgId]);
  const handleToggleMaterialsTab = useCallback(() => {
    setActiveTab(prev => (prev === 'materials' ? 'conversation' : 'materials'));
  }, []);
  const handleToggleMobileMenu = useCallback(() => {
    setShowMobileMenu(prev => !prev);
  }, []);
  const handleCloseMobileMenuBackdrop = useCallback(() => {
    setShowMobileMenu(false);
  }, []);
  const handleMobileMaterialsToggleAndCloseMenu = useCallback(() => {
    setActiveTab(prev => (prev === 'materials' ? 'conversation' : 'materials'));
    setShowMobileMenu(false);
  }, []);
  const handleMobileOpenShareModalAndCloseMenu = useCallback(() => {
    setIsShareModalOpen(true);
    setShowMobileMenu(false);
  }, []);

  // Copy feedback state (tracks which chain was just copied)
  const [copiedChainId, setCopiedChainId] = useState<string | null>(null);

  // Regenerating chain state (tracks which chain is being regenerated)
  const [regeneratingChainId, setRegeneratingChainId] = useState<string | null>(
    null
  );

  const {
    sidebarDynamicViewId,
    sidebarChainId,
    isAnimating,
    sidebarWidth,
    isResizing,
    handleOpenDynamicViewSidebar,
    handleOpenReasoningSidebar,
    handleCloseSidebar,
    handleResizeStart,
    resizeSidebarBy,
    closeReasoningSidebar,
  } = useReasoningSidebarPanel();

  const {
    chainFeedback,
    feedbackModalOpen,
    feedbackModalChainId,
    feedbackModalSteps,
    handleFeedback,
    handleStructuredFeedback,
    handleFeedbackModalClose,
  } = useChainFeedbackModal({ orgId, conversationId, chains });

  // Proposal sidebar hook for opening proposals
  const {
    openProposalId,
    sidebarWidth: proposalSidebarWidth,
    isResizing: proposalIsResizing,
    isSidebarVisible: proposalSidebarVisible,
    proposalUpdateCounter,
    openProposal,
  } = useProposalSidebar();
  const queryClient = useQueryClient();

  // Builder mode: the object being built sits beside the chat rather than over
  // it, so the chat column gives up width. The panel is a real child of the
  // layout (see BuilderSplit) — not the floating entity slideout it used to
  // borrow — so "push" is purely this column shrinking against a real sibling.
  const { isBuilderMode } = useBuilderMode();
  const {
    width: slideoutWidth,
    setWidth: setSlideoutWidth,
    isResizing: slideoutIsResizing,
    setIsResizing: setSlideoutIsResizing,
  } = useSlideoutWidth();

  // The conversation is the authority on what it is; the route is a rendering
  // hint that must follow it. Reconciling here is what makes a builder
  // conversation impossible to open "incorrectly" — arriving from the
  // conversation list, a bookmark or a shared link lands on the right route
  // instead of silently rendering a chat with no panel.
  const builderOrgId =
    orgId !== undefined ? parseInt(orgId, 10) || undefined : undefined;
  React.useEffect(() => {
    if (builderOrgId === undefined || conversationId === undefined) {
      return;
    }
    const target = reconcileBuilderRoute({
      orgId: builderOrgId,
      conversationId,
      agentType: currentConversation?.agent_type,
      isBuilderRoute: isBuilderMode,
      // Load-bearing. Without it the redirect below lands on a bare pathname
      // and, being `replace: true`, erases every query param with no history
      // entry to recover them from — closing an open `?slideoutEntityId=`
      // record. Same hazard the nav-state effect further down already guards.
      search: location.search,
    });
    if (target) {
      navigate(target, { replace: true });
    }
  }, [
    builderOrgId,
    conversationId,
    currentConversation?.agent_type,
    isBuilderMode,
    location.search,
    navigate,
  ]);

  // What the view renders is decided by the conversation, not the URL. Before
  // the conversation loads the route is the only hint available — and it is
  // the route we navigated from — so it stands in for that window only. Once
  // `agent_type` arrives it wins outright, which is what makes opening a
  // builder conversation from the conversation list, a bookmark or a shared
  // link land in builder mode rather than a chat with a missing panel.
  const isBuilderConversationView =
    conversationId === undefined ||
    currentConversation?.agent_type === undefined
      ? isBuilderMode
      : currentConversation.agent_type === ENTITY_BUILDER_AGENT_TYPE;

  // Fail closed until a live fetch resolves: the persisted-cache fast path in
  // `useFeatureFlags` would otherwise let a stale/errored value flash the inline
  // variant for non-builder chats, violating the default-off/no-flash contract.
  const isInlineProposalsEnabled = useFeatureFlag('inline_proposals', {
    failClosedUntilFresh: true,
  });
  const showInline = isInlineProposalsEnabled || isBuilderConversationView;

  // Proposals created anywhere in this conversation, oldest first. Under the
  // V1 guidance-only invariant the agent is asked to revise the existing
  // proposal rather than issue a new one, but nothing enforces it — so take
  // the newest and treat earlier ones as superseded.
  //
  // Deduplicated, LAST occurrence wins: `update_proposed_action` re-lists an
  // existing proposal on the step that edited it, so a proposal touched after
  // a newer one was created is the more recently worked-on draft. Keeping one
  // entry per id also makes the hook's "is the selection still a candidate"
  // membership check exact.
  const builderProposalIds = React.useMemo(() => {
    if (!isBuilderConversationView) {
      return [];
    }
    const ordered = chains.flatMap(chain =>
      (chain.steps || []).flatMap(step =>
        (step.created_proposals || []).map(proposal => proposal.id)
      )
    );
    const lastIndexById = new Map<number, number>();
    ordered.forEach((id, index) => lastIndexById.set(id, index));
    return ordered.filter((id, index) => lastIndexById.get(id) === index);
  }, [isBuilderConversationView, chains]);

  // Ordinary chats: the most recently created/touched proposal, by the same
  // chronology builder mode uses (chains and steps are ordered; the last
  // listed occurrence is the most recent — see builderProposalIds above).
  // Drives the docked pane's auto-open.
  const latestProposalId = React.useMemo(
    () =>
      isBuilderConversationView
        ? undefined
        : lastCreatedProposalId(chains, false),
    [isBuilderConversationView, chains]
  );

  // The most recent proposal that is still OPEN (pending) — the fallback for
  // which proposal a message is "about" when none is explicitly open in the
  // panel. Deliberately a separate rule from latestProposalId above: auto-open
  // shows the newest proposal regardless of resolution (builder semantics),
  // while "the proposal" in a user's message can only mean one that is still
  // actionable. The client's resolution copy can be momentarily stale after
  // an approve; that is fine — the backend re-fetches at grounding time and
  // states the true resolution, so a stale pick self-heals server-side.
  const latestOpenProposalId = React.useMemo(
    () =>
      isBuilderConversationView
        ? undefined
        : lastCreatedProposalId(chains, true),
    [isBuilderConversationView, chains]
  );

  // The user's override of which proposal drives the builder panel — set by
  // clicking an inline proposal card when the conversation has more than one.
  // `undefined` means "no override," which is what keeps the create flow
  // unchanged: the default stays "most recent," never "most recent pending."
  const [selectedProposalId, setSelectedProposalIdState] = useState<
    number | undefined
  >(undefined);
  const handleSelectProposal = useCallback((proposalId: number) => {
    setSelectedProposalIdState(proposalId);
  }, []);

  const {
    entityId: builderSubjectId,
    entityTypeName: builderSubjectEntityTypeName,
    draft: builderDraft,
    overlays: builderOverlays,
    mostRecentProposalId: mostRecentBuilderProposalId,
  } = useBuilderSubject({
    orgId: builderOrgId,
    proposalIds: builderProposalIds,
    // The conversation records its own subject, so the panel no longer
    // depends on a URL param surviving navigation.
    pinnedEntityId: currentConversation?.builder_entity_id ?? undefined,
    // Always set on a builder conversation, and unlike the draft it survives
    // approval — without it the panel loses the entity type the moment the
    // build is saved.
    pinnedEntityTypeName: currentConversation?.builder_entity_type ?? undefined,
    selectedProposalId,
  });

  // Builder conversations get a placeholder describing what can be built here;
  // ordinary chat keeps the generic one. `hasSubject` counts the draft as well
  // as the saved record — the moment the agent proposes something, the panel
  // has content and the next message refines it rather than starts it.
  const followUpPlaceholder = isBuilderConversationView
    ? getBuilderPlaceholder({
        entityTypeName: builderSubjectEntityTypeName,
        hasSubject: Boolean(builderSubjectId || builderDraft),
      })
    : DEFAULT_FOLLOW_UP_PLACEHOLDER;

  // A new proposal is the agent answering the user — pinning the panel to a
  // selection made before that answer arrived would hide it. Clearing here
  // (rather than never clearing) is what keeps a stale selection from
  // silently outliving the turn it was made in.
  const prevMostRecentBuilderProposalIdRef = useRef(
    mostRecentBuilderProposalId
  );
  useEffect(() => {
    if (
      mostRecentBuilderProposalId !== prevMostRecentBuilderProposalIdRef.current
    ) {
      prevMostRecentBuilderProposalIdRef.current = mostRecentBuilderProposalId;
      setSelectedProposalIdState(undefined);
    }
  }, [mostRecentBuilderProposalId]);

  // Reserve the strip for the whole session, not just once there is something
  // to put in it. The build route already shows the panel (with its own empty
  // state) before the first message, so gating on the subject here would make
  // the panel vanish the instant the user sends and reappear when the agent's
  // proposal lands — the layout jumping twice around a single action. The
  // panel renders its own empty state in the gap.
  // Gated on the SAME breakpoint the panel's own `hidden lg:flex` uses. The
  // panel's visibility is CSS-driven and this width reservation is JS-driven;
  // when the two disagree below `lg` the panel vanishes while the chat column
  // still gives up ~600px, squeezing the thread to nothing beside an empty
  // gap. Keep these two in lockstep.
  const isLargeScreen = useBreakpoint('lg');
  const builderPanelVisible = isBuilderConversationView && isLargeScreen;
  // Below `lg` in builder mode: the record is the primary surface; the chat
  // thread + composer are docked at the bottom. Mutually exclusive with
  // `builderPanelVisible` (one is !isLargeScreen, the other is isLargeScreen),
  // so only ONE BuilderPanel is ever in the DOM at a time.
  const isMobileBuilderView = isBuilderConversationView && !isLargeScreen;

  // ── Docked proposal pane ─────────────────────────────────────────────────
  // Proposals open SIDE BY SIDE with the chat (like the builder record
  // panel), not as the global slideout overlay. This page claims the
  // `proposal` kind via registerInlineHost while the viewport has room; the
  // global SlideoutRenderer stands down and the pane renders as a flex
  // sibling below. Dropping the claim (narrow viewport, builder mode) hands
  // rendering back to the global overlay automatically — the same seam
  // ActionDetailPage uses for `entity`. Optional context, like that page:
  // without a SlideoutStackProvider there is simply no docked pane.
  const slideoutStackContext = useSlideoutStackContextOptional();
  const topEntry = slideoutStackContext?.topEntry;
  const registerInlineHost = slideoutStackContext?.registerInlineHost;

  // The whole side-by-side treatment (docked pane, auto-open, entity-record
  // variant with its hidden in-panel chat) ships behind `builder_mode`, the
  // same gate as the rest of the builder surface area. Deliberately the
  // PERSISTED fast path, not failClosedUntilFresh (unlike `inline_proposals`
  // above): the claim below must be decidable on the FIRST render, or
  // navigating to a chat with a proposal on the stack paints the global
  // overlay + backdrop for the flag-fetch window and then swaps to the
  // docked pane — a visible flash. The stale-cache worst case here (a
  // just-gated org briefly docking the pane before the fresh flag flips it
  // back to the overlay) is rarer and milder than flashing the overlay for
  // every enabled org on every navigation. Flag off ⇒ no inline-host claim ⇒
  // every proposal click falls through to the global overlay, exactly the
  // pre-feature flow.
  const isBuilderModeGateEnabled = useFeatureFlag('builder_mode');
  const hasRoomForDockedProposal = useCanHostInlinePanel(
    MIN_CHAT_WIDTH_FOR_DOCKED_PROPOSAL
  );
  const canHostInlineProposal =
    isBuilderModeGateEnabled &&
    !isBuilderConversationView &&
    hasRoomForDockedProposal;

  // Layout effect, not a plain effect: it runs after commit but BEFORE the
  // browser paints, so when this page mounts with a proposal already on the
  // stack (deep link, navigation from a page showing the overlay), the claim
  // is registered before the global SlideoutRenderer ever gets a painted
  // frame — no overlay/backdrop flash on the way to the docked pane.
  useLayoutEffect(() => {
    if (!canHostInlineProposal || !registerInlineHost) {
      return;
    }
    return registerInlineHost(['proposal']);
  }, [canHostInlineProposal, registerInlineHost]);

  const dockedProposalEntry =
    canHostInlineProposal && topEntry?.kind === 'proposal' ? topEntry : null;

  // The docked pane and the reasoning/dynamic-view sidebar compete for the
  // same right-hand space; docking a proposal closes them (external stack
  // state driving the close — the ActionDetailPage toggleListSidebar
  // precedent).
  useEffect(() => {
    if (dockedProposalEntry) {
      handleCloseSidebar();
    }
  }, [dockedProposalEntry, handleCloseSidebar]);

  const {
    onResizeStart: handleProposalPaneResizeStart,
    resizeBy: resizeProposalPaneBy,
  } = useProposalPaneResize(
    setSlideoutWidth,
    setSlideoutIsResizing,
    slideoutWidth
  );

  useAutoOpenLatestProposal(
    canHostInlineProposal,
    latestProposalId,
    topEntry,
    openProposal
  );

  // Names what the mobile panel contains ("View automation"). Falls back to a
  // neutral noun rather than "undefined" while the subject resolves.
  const builderRecordLabel = builderSubjectEntityTypeName
    ? getTypeLabelByName(builderSubjectEntityTypeName).toLowerCase()
    : 'record';

  // Counted the same way the record surface counts it for the SaveBar —
  // proposed properties on the subject's overlay. Keeping one definition means
  // the review bar and the save toolbar it summons can never disagree about
  // how much is pending.
  const builderPendingChangeCount = useMemo(
    () => Object.keys(builderDraft?.overlay.properties ?? {}).length,
    [builderDraft?.overlay.properties]
  );

  // MOBILE ONLY. The compact composer (ConversationInput variant='simple') is
  // the same one the proposal side panel and email composer use, so phones get
  // one consistent input rather than a hero box squeezed into a narrow screen.
  // Desktop keeps the hero at every width, builder included.
  const useCompactComposer = !isLargeScreen;

  // Mobile only: the record panel opens FULL SCREEN over the chat. Closed by
  // default so the conversation is what the user lands in — the panel is a
  // review surface you go to, not the thing you start in.
  const [isMobilePanelOpen, setIsMobilePanelOpen] = useState(false);
  const openMobilePanel = useCallback(() => setIsMobilePanelOpen(true), []);
  const closeMobilePanel = useCallback(() => setIsMobilePanelOpen(false), []);

  // Leaving the mobile width with the overlay open would otherwise strand that
  // state: the desktop layout renders the panel inline and never reads it, so
  // returning to mobile would pop the overlay open unbidden.
  useEffect(() => {
    if (isLargeScreen) {
      setIsMobilePanelOpen(false);
    }
  }, [isLargeScreen]);

  // Static animation styles — injected once, use CSS custom properties for dynamic values.
  // Previously this was re-injected on every sidebarWidth change, creating a new <style>
  // element ~60 times/sec during resize which caused severe lag.
  React.useEffect(() => {
    const style = document.createElement('style');
    style.setAttribute('data-reasoning-chat', '');
    style.textContent = `
      @keyframes chatSatisfyingPush {
        0% { width: 100%; }
        55% { width: calc(100% - var(--sidebar-target-width) - 2%); }
        100% { width: calc(100% - var(--sidebar-target-width)); }
      }

      @keyframes sidebarSmoothSlide {
        0% { width: 0; opacity: 0; }
        55% { width: calc(var(--sidebar-target-width) + 2%); opacity: 0.9; }
        100% { width: var(--sidebar-target-width); opacity: 1; }
      }

      /* Global resize feedback */
      .is-resizing-sidebar * {
        pointer-events: none !important;
      }

      .is-resizing-sidebar {
        cursor: col-resize !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Handle copy to clipboard with visual feedback (copies as rich text)
  const handleCopy = async (chainId: string, content: string | undefined) => {
    if (!content) {
      return;
    }
    const success = await copyToClipboard(content);
    if (success) {
      setCopiedChainId(chainId);
      setTimeout(() => setCopiedChainId(null), 2000);
    }
  };

  // Handle chain regeneration (refresh/retry)
  const handleRetry = async (chainId: string) => {
    if (!orgId || !conversationId || regeneratingChainId) {
      return;
    }

    setRegeneratingChainId(chainId);
    setError(null);

    try {
      const result = await conversationService.regenerateChain(
        orgId,
        conversationId,
        chainId
      );

      // Start polling for the new chain
      setCurrentTaskId(result.task_id);
      // Register with the tracker so completion/failure toasts survive the
      // page unmounting mid-regeneration. Key MUST match the visible/hidden
      // registration key (the conversationId route param) so toast
      // suppression holds while the surface is on screen.
      if (result.task_id) {
        trackChatRun({ orgId, conversationId, taskId: result.task_id });
      }
      setProgressStatus('Retrying…');

      // Reload conversation to get the updated chain status
      await loadConversation(conversationId);
    } catch (err) {
      console.error('Failed to regenerate chain:', err);
      const errorMessage = (err instanceof Error && err.message) || '';

      // Map error messages to user-friendly text
      if (errorMessage.includes('only regenerate the last')) {
        setError('Can only retry the most recent response');
      } else if (errorMessage.includes('still processing')) {
        setError('Please wait for the current response to complete');
      } else if (errorMessage.includes('404')) {
        setError('Response not found');
      } else if (errorMessage.includes('403')) {
        setError("You don't have access to this conversation");
      } else {
        setError(
          'Something went wrong. Try again, and get in touch if it keeps happening.'
        );
      }
    } finally {
      setRegeneratingChainId(null);
    }
  };

  const {
    hasNewContent,
    scrollToBottom,
    handleScroll,
    handleNewMessagesClick,
    notifyNewContentIfScrolledUp,
  } = useChatAutoScroll({
    containerRef: chatContainerRef,
    conversationId,
    chains,
    partialSteps,
    currentConversation,
    currentTaskId,
  });

  // Redirect to conversations home when no conversation is selected
  useEffect(() => {
    if (!currentConversation && !conversationId && orgId) {
      navigate(`/${orgId}/conversations`);
    }
  }, [currentConversation, conversationId, orgId, navigate]);

  const loadConversation = useCallback(
    async (id: number | string) => {
      if (!orgId) {
        return;
      }

      // Claim the latest generation. Any load still in flight when this runs is
      // now stale and must not mutate UI state once it resolves.
      const loadGeneration = (loadGenerationRef.current += 1);

      setError(null);
      setPollingFailed(false);

      // True when this call refreshes the conversation already on screen
      // (e.g. the automatic reload after a reasoning task completes), as
      // opposed to an initial load, a switch to a different conversation,
      // or a reload triggered while no conversation is visible.
      const isRefreshOfRenderedConversation =
        renderedConversationIdRef.current === String(id);

      let failureDetail = '';
      for (
        let attempt = 1;
        attempt <= LOAD_CONVERSATION_MAX_ATTEMPTS;
        attempt++
      ) {
        try {
          const response = await fetchWithDefaults(
            `/api/orgs/${orgId}/reasoning/conversations/${id}/`
          );

          if (response.ok) {
            const data = await response.json();

            // A rapid conversation switch (A → B) can leave loadConversation(A)
            // resolving after the route already moved to B (this component stays
            // mounted; only the param changes, and there is no fetch abort).
            // Two guards, both required:
            //   1. Generation: a newer load (including the A → B → A case, where
            //      the id check below passes because the stale id matches the
            //      route again) has superseded this one — bail.
            //   2. Route mismatch: Effect 1 updates previousConversationIdRef
            //      synchronously on every switch, so it is the current route's
            //      conversation; a response for a different conversation must not
            //      render A over B or point useChatProgress at A's task id.
            if (
              loadGenerationRef.current !== loadGeneration ||
              (previousConversationIdRef.current !== null &&
                previousConversationIdRef.current !==
                  String(data.conversation.id))
            ) {
              return;
            }

            // Record this as the newest successful load so a still-in-flight
            // OLDER load that later fails cannot clobber this rendered content
            // with a stale error (see the failure guard below).
            lastSucceededLoadGenerationRef.current = loadGeneration;

            setCurrentConversation(data.conversation);
            setChains(data.chains || []);

            // A completed run may have EDITED a proposal in place
            // (update_proposed_action): same id, new payload, and no
            // websocket event covers that path — so an open detail surface
            // (the docked pane, the builder panel) keeps rendering the
            // pre-edit payload for up to the query's staleTime. On a refresh
            // of the on-screen conversation, mark every listed proposal's
            // detail stale; the default refetchType ('active') means only a
            // MOUNTED detail refetches — one request per open proposal,
            // none for closed ones, nothing on initial loads.
            if (isRefreshOfRenderedConversation) {
              const listedIds = new Set<number>(
                (data.chains || []).flatMap((chain: Chain) =>
                  (chain.steps || []).flatMap(step =>
                    (step.created_proposals || []).map(p => p.id)
                  )
                )
              );
              for (const listedId of listedIds) {
                void queryClient.invalidateQueries({
                  queryKey: proposalQueryKeys.detail(
                    parseInt(orgId, 10),
                    listedId
                  ),
                });
              }
            }

            // Resume polling if returning to a conversation with active processing
            // (e.g., after page refresh or navigating back while reasoning was in progress)
            // Only set if NOT already polling (use ref to avoid stale closure)
            const activeTaskId = findActiveProcessingTask(data.chains || []);
            if (activeTaskId && !currentTaskIdRef.current) {
              setCurrentTaskId(activeTaskId);
              setPollingConversationId(String(data.conversation.id));
              setProgressStatus('Resuming…');
              // Register the resumed run with the tracker so the nav badge
              // reflects it and an away-completion/failure toast still fires
              // after this reload/back-navigation. Idempotent: registerChatRun
              // no-ops when the same key+taskId is already tracked. Key matches
              // the visible/hidden registration (the conversation id) so toast
              // suppression holds while this surface is on screen.
              trackChatRun({
                orgId,
                conversationId: String(data.conversation.id),
                taskId: activeTaskId,
              });
            }

            if (data.chains && data.chains.length > 0) {
              // Collapse all chains by default when new responses arrive
              const allChainIds = data.chains.map((chain: Chain) => chain.id);
              setCollapsedChains(new Set(allChainIds));

              // Auto-collapse all completed non-final steps
              const completedStepIds = data.chains
                .flatMap((chain: Chain) => chain.steps)
                .filter(
                  (step: ReasoningStep) =>
                    step.status === 'completed' && !step.is_final
                )
                .map((step: ReasoningStep) => step.id);
              setCollapsedSteps(new Set(completedStepIds));
            }
            return;
          }

          failureDetail = `HTTP ${response.status}`;
          if (response.status < 500 && response.status !== 429) {
            break;
          }
        } catch (err) {
          failureDetail =
            err instanceof Error ? `${err.name}: ${err.message}` : String(err);
        }

        if (attempt < LOAD_CONVERSATION_MAX_ATTEMPTS) {
          await wait(LOAD_CONVERSATION_RETRY_DELAY_MS);
        }
      }

      // Two independent reasons to suppress this failure:
      //   1. A strictly-NEWER load already SUCCEEDED. Overlapping refreshes of
      //      the SAME conversation share a route, so the route check below can't
      //      catch this: if load N+1 rendered fresh content and then this older
      //      load N fails, surfacing its error toast/banner would wrongly report
      //      a failure over content the user is actually looking at. Gate on the
      //      last SUCCEEDED generation (not merely the latest STARTED one): when
      //      overlapping loads all fail — e.g. a poll-driven refresh loop that
      //      keeps retriggering while each retry is still failing — none of them
      //      succeeded, so the failure the user must see is still surfaced.
      //   2. Route mismatch: the route moved away (A → B) while this load for A
      //      was in flight (component stays mounted; only the param changes; no
      //      fetch abort). Keyed on the requested id since there is no response
      //      body to read the conversation id from. A genuine same-conversation
      //      refresh (route still A) still surfaces the toast.
      if (
        lastSucceededLoadGenerationRef.current > loadGeneration ||
        (previousConversationIdRef.current !== null &&
          previousConversationIdRef.current !== String(id))
      ) {
        return;
      }

      console.error(`Failed to load conversation ${id}: ${failureDetail}`);
      if (isRefreshOfRenderedConversation) {
        // The user is still looking at valid content — a blocking banner
        // would wrongly suggest the conversation is gone. The next send,
        // poll cycle, or navigation reloads it anyway.
        toast.error("Couldn't refresh the conversation", {
          id: 'conversation-refresh-failed',
        });
      } else {
        setError("We couldn't load this conversation.");
      }
    },
    [orgId, queryClient]
  );

  // Track viewport size for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setShowMobileMenu(false); // Close menu on resize
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Effect 1: Load conversation when ID changes (stable dependencies)
  useEffect(() => {
    loadConversations();

    // Switching directly between conversations (e.g. from one still-running
    // conversation to another via the sidebar/history) keeps this component
    // mounted and only changes the route param. Without clearing it, the
    // outgoing conversation's task lingers in currentTaskId/currentTaskIdRef,
    // which (a) makes the adopt guards in loadConversation and Effect 3 skip
    // the destination's active run — so it is never registered with the
    // tracker — and (b) points useChatProgress at the new conversation with the
    // old task id. Reset the task-tracking state on every genuine switch so the
    // destination's run is adopted fresh. Follow-ups reuse the same
    // conversationId and never re-run this effect; the nav-state taskId path
    // (Effect 2) runs after this effect and re-sets the task for that flow.
    const isConversationSwitch =
      previousConversationIdRef.current !== (conversationId ?? null);
    if (isConversationSwitch) {
      setCurrentTaskId(null);
      currentTaskIdRef.current = null;
      setPollingConversationId(null);
      setProgressStatus('');
      setPartialSteps([]);
    }
    previousConversationIdRef.current = conversationId ?? null;

    if (conversationId) {
      loadConversation(conversationId);
    } else {
      setCurrentConversation(null);
    }

    // Drop the reasoning sidebar: its chain belongs to the conversation we are
    // leaving. The dynamic view needs nothing here — it follows `?view=`, so
    // the URL we just arrived at already says whether one should be open, and
    // closing it by hand would discard a deep link the user had followed.
    closeReasoningSidebar();
    setActiveTab('conversation');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: load conversations once on initial render
  }, [conversationId, loadConversation]);

  // Re-read the conversation after the builder panel resolves a draft, by
  // Save OR Cancel.
  //
  // Save: on the create path the record's id reaches the client only on the
  // conversation row, written server-side at approval. Approving also resolves
  // the proposal, which drops the draft — so without this re-read the page
  // holds the `builder_entity_id: null` it loaded with, the panel has neither a
  // draft nor an id, and it renders its empty state over a record that exists.
  // Reloading the page used to be the only way out.
  //
  // Cancel: rejecting resolves the proposal too, but chat-built proposals get
  // no websocket update, so each card's `resolution` (from `created_proposals`)
  // is only as fresh as the last conversation read. Without this the rejected
  // card keeps reading "pending" until the next reload.
  const reloadConversationAfterResolution = useCallback(() => {
    if (conversationId) {
      loadConversation(conversationId);
    }
  }, [conversationId, loadConversation]);

  // Effect 2: Handle navigation state for polling (separate to avoid re-triggering)
  useEffect(() => {
    const state = location.state as {
      taskId?: string;
      fromAskAhoy?: boolean;
      fromConversationsHome?: boolean;
      userMessage?: string;
      initialPrompt?: string;
    } | null;

    // Handle taskId from navigation (new conversation started)
    if (
      state?.taskId &&
      (state?.fromAskAhoy || state?.fromConversationsHome) &&
      state.taskId !== processedTaskIdRef.current
    ) {
      // Mark this taskId as processed to avoid re-processing
      processedTaskIdRef.current = state.taskId;

      // Start polling for the task
      setCurrentTaskId(state.taskId);
      setPollingConversationId(conversationId || null);
      setProgressStatus('Starting reasoning…');

      // Set optimistic message from navigation state for instant UI feedback
      if (state.userMessage) {
        setOptimisticMessage(state.userMessage);
      }

      // Clear the navigation state. Keep `location.search`: navigating to the
      // bare pathname drops every search param, including `?slideoutEntityId=`
      // — so arriving here with a record open would silently close it.
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: {},
      });
    }

    // Handle initialPrompt from navigation (pre-fill input)
    if (state?.initialPrompt && !conversationId) {
      navigate(`${location.pathname}${location.search}`, {
        replace: true,
        state: {},
      });
    }
  }, [
    location.state,
    conversationId,
    navigate,
    location.pathname,
    location.search,
  ]);

  // Effect 3: Fallback to ensure polling starts if chains have an active task
  // This catches any race conditions or timing issues with initial load
  useEffect(() => {
    // Only adopt from `chains` once they belong to the conversation in the URL.
    // On a switch A -> B, Effect 1 nulls currentTaskId synchronously but the
    // outgoing conversation's chains linger until loadConversation(B) resolves
    // and replaces both `currentConversation` and `chains` together. Without
    // this gate, Effect 3 would re-adopt A's active task against B's id, poll B
    // with A's stale task id, and leave B's real run unregistered.
    const chainsMatchConversation =
      String(currentConversation?.id ?? '') === String(conversationId ?? '');
    if (!chainsMatchConversation) {
      return;
    }
    if (!currentTaskId && chains.length > 0) {
      const activeTaskId = findActiveProcessingTask(chains);
      if (activeTaskId) {
        setCurrentTaskId(activeTaskId);
        setPollingConversationId(conversationId || null);
        setProgressStatus('Resuming…');
        // Register the adopted run so the nav badge and away-completion/failure
        // toasts keep working for a run resumed via this fallback path.
        // Idempotent when the run is already tracked.
        if (orgId && conversationId) {
          trackChatRun({ orgId, conversationId, taskId: activeTaskId });
        }
      }
    }
  }, [chains, currentTaskId, conversationId, orgId, currentConversation]);

  // Effect 4: Clear optimistic message when chains load (real data replaces optimistic UI)
  useEffect(() => {
    if (chains.length > 0) {
      setOptimisticMessage(null);
    }
  }, [chains.length]);

  // Effect 5: Reload conversation when proposal sidebar completes AI chat
  // The proposalUpdateCounter increments when ProposalDetailPanel.onComplete fires
  // This ensures the conversation chains update after AI processes proposal edits
  useEffect(() => {
    // Skip initial mount (counter starts at 0)
    if (proposalUpdateCounter === 0) {
      return;
    }

    if (conversationId) {
      loadConversation(conversationId);
    }
  }, [proposalUpdateCounter, conversationId, openProposalId, loadConversation]);

  // WS event handler: step.progress maps directly into the existing partial-steps
  // dedup logic. We funnel the WS event into the same setPartialSteps reducer
  // by constructing a one-step array and calling the same comparator pathway —
  // simplest reuse of the existing 500ms-poll codepath at lines 1546-1564.
  const onWsStepProgress = useCallback(
    (event: StepProgressEvent) => {
      const incomingStep: ReasoningStepData = {
        id: event.step.id,
        step_number: event.step.step_number,
        stage: event.step.stage,
        status: event.step.status,
        content: event.step.content,
        is_final: event.step.is_final,
        tool_results: event.step.tool_results,
      };
      setPartialSteps(prev => {
        // Replace-or-append by id. Same id => replace in place; new id => append.
        const idx = prev.findIndex(p => p.id === incomingStep.id);
        if (idx === -1) {
          return [...prev, incomingStep];
        }
        const existing = prev[idx];
        const unchanged =
          existing.status === incomingStep.status &&
          existing.is_final === incomingStep.is_final &&
          existing.content === incomingStep.content &&
          (existing.tool_results?.length ?? 0) ===
            (incomingStep.tool_results?.length ?? 0) &&
          toolStatusSignature(existing.tool_results) ===
            toolStatusSignature(incomingStep.tool_results);
        if (unchanged) {
          return prev;
        }
        const next = prev.slice();
        next[idx] = incomingStep;
        return next;
      });
      notifyNewContentIfScrolledUp();
    },
    [notifyNewContentIfScrolledUp]
  );

  const onWsChainCompleted = useCallback(
    async (_event: ChainCompletedEvent) => {
      // Mirror the data.complete && data.success branch of onPollingUpdate.
      // Clear task/polling state BEFORE awaiting the reload so the polling
      // loop can't fire a second completion handler against the now-finished
      // task while loadConversation is in flight.
      setCurrentTaskId(null);
      setPollingConversationId(null);
      if (conversationId) {
        await loadConversation(conversationId);
      }
      setProgressStatus('');
      setPartialSteps([]);
      setLoading(false);
    },
    [conversationId, loadConversation]
  );

  const onWsChainFailed = useCallback((event: ChainFailedEvent) => {
    // Mirror the data.complete && !data.success branch of onPollingUpdate.
    setError(`Reasoning failed: ${event.error || 'Unknown error'}`);
    setCurrentTaskId(null);
    setPollingConversationId(null);
    setProgressStatus('');
    setPartialSteps([]);
    setLoading(false);
  }, []);

  const onWsTitleUpdated = useCallback((event: TitleUpdatedEvent) => {
    setCurrentConversation(prev =>
      prev ? { ...prev, title: event.title } : prev
    );
  }, []);

  // Memoize polling callbacks with stable dependencies to prevent polling restarts
  const onPollingUpdate = useCallback(
    async (data: {
      complete?: boolean;
      success?: boolean;
      error?: string;
      result?: { error?: string };
      progress?: { description?: string };
      description?: string;
      partial_steps?: ReasoningStepData[];
    }) => {
      if (data.complete) {
        if (data.success) {
          // Task completed successfully - reload conversation
          // Use conversationId from URL params (stable) instead of state
          if (conversationId) {
            await loadConversation(conversationId);
          }
        } else {
          // Task failed
          setError(
            `Reasoning failed: ${data.result?.error || 'Unknown error'}`
          );
        }
        setCurrentTaskId(null);
        setPollingConversationId(null);
        setProgressStatus('');
        setPartialSteps([]);
        setLoading(false);
      } else {
        // Update progress status
        // celery-progress puts description in nested 'progress' object
        const description =
          data.progress?.description || data.description || 'Thinking…';
        // setProgressStatus already no-ops when the string is unchanged.
        setProgressStatus(description);

        // partial_steps is at root level from our backend enhancement
        const steps = data.partial_steps;
        if (steps && steps.length > 0) {
          // Polls fire every 500ms but the data only changes at step/content
          // boundaries. Bail out (return prev) when nothing meaningful changed
          // so React skips the re-render — otherwise the whole chat tree
          // re-renders twice a second on a brand-new array reference.
          setPartialSteps(prev => {
            const unchanged =
              prev.length === steps.length &&
              prev.every((p, i) => {
                const next = steps[i];
                return (
                  p.id === next.id &&
                  p.status === next.status &&
                  p.is_final === next.is_final &&
                  p.content === next.content &&
                  (p.tool_results?.length ?? 0) ===
                    (next.tool_results?.length ?? 0) &&
                  // Detect per-tool status flips (running -> done/error) that don't
                  // change tool_results length but must still trigger a re-render.
                  toolStatusSignature(p.tool_results) ===
                    toolStatusSignature(next.tool_results)
                );
              });
            return unchanged ? prev : steps;
          });

          // The partialSteps effect surfaces the "New messages" button for a
          // scrolled-up user, but it only re-runs when the steps reference
          // changes — and the bail-out above intentionally keeps that
          // reference stable. So flag new content directly here on every poll
          // that carries steps; otherwise a scrolled-up user would never see
          // the button while a chain is mid-stream. (No-ops once already true.)
          notifyNewContentIfScrolledUp();
        }
      }
    },
    [conversationId, loadConversation, notifyNewContentIfScrolledUp]
  );

  const onPollingError = useCallback((error: string) => {
    console.error('Error polling progress:', error);
    setCurrentTaskId(null);
    setPollingConversationId(null);
    setProgressStatus('');
    setPartialSteps([]);
    setLoading(false);
    setError("We couldn't get progress updates.");
    setPollingFailed(true);
  }, []);

  // Set up real-time progress: WebSocket-first delivery with degraded polling
  // fallback. The hook owns transport coordination — when WS is connected,
  // polling auto-degrades from 500ms to 5s; when WS disconnects, it restores
  // to 500ms. Polling is gated on currentTaskId (same as before) but the WS
  // connects whenever conversationId is set so it's ready to receive
  // step.progress events the moment reasoning starts.
  const { wsStatus, isPolling } = useChatProgress({
    orgId,
    conversationId: conversationId || pollingConversationId,
    taskId: currentTaskId,
    onStepProgress: onWsStepProgress,
    onChainCompleted: onWsChainCompleted,
    onChainFailed: onWsChainFailed,
    onTitleUpdated: onWsTitleUpdated,
    onPollingUpdate,
    onPollingError,
    enabled: !!conversationId || !!pollingConversationId,
  });

  // Route health: polling is "ready" when either a task is actively being
  // polled (isPolling === true) or no task exists that requires polling.
  // Gating on isPolling alone would never fire for conversations without an
  // active task; gating on conversation-data alone reported the route healthy
  // before the polling transport had actually been evaluated. `pollingFailed`
  // is a dedicated signal (set only in onPollingError) so unrelated UI errors
  // — regenerate/follow-up failures, load errors from other requests — do not
  // suppress the marker and produce false timeout beacons.
  useViewHealthMarker('polling-ready', {
    ready:
      currentConversation !== null &&
      String(currentConversation.id) === String(conversationId) &&
      (isPolling ||
        wsStatus === 'connected' ||
        (!currentTaskId && !pollingFailed)),
  });

  const continueConversation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!followUpMessage.trim() || !currentConversation || !orgId) {
      return;
    }

    // Capture inputs so we can restore them if the send fails.
    const submittedMessage = followUpMessage;
    const submittedFiles = uploadedFiles;
    const submittedFileIds = submittedFiles.map(f => f.id);

    setLoading(true);
    setError(null);
    // Instant feedback: clear the composer and show the processing card NOW,
    // before the POST round-trip, so there's no dead window where nothing
    // appears to happen. Rolled back below if the send fails.
    setFollowUpMessage('');
    setUploadedFiles([]);
    setProgressStatus('Thinking…');

    const restoreComposer = () => {
      setFollowUpMessage(submittedMessage);
      setUploadedFiles(submittedFiles);
      setProgressStatus('');
    };

    try {
      const response = await fetchWithDefaults(
        `/api/orgs/${orgId}/reasoning/conversations/${currentConversation.id}/messages/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: submittedMessage,
            file_ids: submittedFileIds,
            // The proposal the user most plausibly means by "the proposal":
            // the one open in the panel (docked pane or overlay), else the
            // thread's most recent OPEN proposal; builder conversations send
            // the proposal driving their panel (selection, else most recent).
            // The backend stores it per-chain and re-fetches at grounding
            // time, so a stale pick self-heals server-side. Absent means "no
            // open proposals in this thread".
            proposal_id: isBuilderConversationView
              ? (selectedProposalId ?? mostRecentBuilderProposalId)
              : (openProposalId ?? latestOpenProposalId),
            // In builder mode the PINNED subject rides along with every
            // message, so the user never has to reference the thing they are
            // visibly building. Gated on the subject, NOT on the panel being
            // rendered: collapsing the panel changes what is on screen, not
            // what is being built, and the agent must keep its subject either
            // way. Sent per-message rather than pinned on the conversation, so
            // each turn is stored with the record it was about and a later turn
            // cannot rewrite an earlier one. One record, replacing rather than
            // accumulating.
            // No builder attachment here. A builder conversation records its
            // own subject on the conversation row and the backend grounds
            // every chain from it, so the client no longer re-sends the record
            // with each message and cannot forget to.
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        const taskId = data.task_id || data.chain_id;
        setCurrentTaskId(taskId);
        // Key MUST match the visible/hidden registration key above (the
        // conversationId route param) so decision-4 toast suppression holds.
        if (taskId && conversationId) {
          trackChatRun({ orgId, conversationId, taskId });
        }
        await loadConversation(currentConversation.id);

        // Scroll to bottom after submitting human response
        setTimeout(() => {
          scrollToBottom();
        }, 200);
      } else {
        restoreComposer();
        setError(data.error || "We couldn't submit that response. Try again.");
      }
    } catch (_error) {
      console.error('Failed to submit response:', _error);
      restoreComposer();
      setError("We couldn't submit that response. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const loadConversations = async () => {
    if (!orgId) {
      return;
    }

    try {
      // Include background agents if we're viewing an agent conversation
      const includeBackground = conversationId ? 'true' : 'false';
      const response = await fetchWithDefaults(
        `/api/orgs/${orgId}/reasoning/conversations/?include_background=${includeBackground}`
      );
      const data = await response.json();
      if (response.ok) {
        setConversations(data.conversations || []);
      }
    } catch {
      // Silently handle error - conversations list is not critical
    }
  };

  // This component requires a orgId - redirect if not available
  if (!orgId) {
    return (
      <div className='min-h-full bg-bg-secondary flex items-center justify-center gradient-bg'>
        <div className='text-center'>
          <h1 className='text-2xl font-bold text-fg-primary mb-4'>
            Team required
          </h1>
          <p className='text-fg-tertiary mb-4'>
            This feature requires you to be in a team context.
          </p>
          <Button color='primary' onClick={() => navigate('/organizations')}>
            Select organization
          </Button>
        </div>
      </div>
    );
  }

  // Redirect to conversations home when no conversation is selected
  // The new ConversationsHome component is the primary landing page
  if (!currentConversation && !conversationId) {
    return null; // Show nothing while redirecting (useEffect above handles navigation)
  }

  // Show conversation view when a conversation is selected
  let mainAreaWidth: string | undefined;
  if (isAnimating) {
    mainAreaWidth = undefined;
  } else if (sidebarDynamicViewId || sidebarChainId) {
    mainAreaWidth = `${100 - sidebarWidth}%`;
  } else if (dockedProposalEntry || builderPanelVisible) {
    // Docked panes (proposal or builder record) are real flex siblings sized
    // by the shared slideout width — the chat column gives up exactly that.
    mainAreaWidth = `calc(100% - ${slideoutWidth}px)`;
  } else if (proposalSidebarVisible) {
    // Overlay fallback (narrow viewport, or this page couldn't claim the
    // inline host): the chat shrinks beside the portaled ProposalSidebar.
    mainAreaWidth = `calc(100% - ${proposalSidebarWidth}px)`;
  } else {
    mainAreaWidth = '100%';
  }

  return (
    <div className='h-full p-0 m-0 overflow-hidden flex flex-col'>
      {/* Mobile builder: the CHAT is the full-screen primary surface. The
          record panel is not stacked above it — it opens over the chat on
          demand (see the overlay below), because a permanently-docked chat
          left too little room for either surface to be usable. */}
      <div
        className='flex flex-1 h-full overflow-hidden p-0 m-0'
        style={{ ['--sidebar-target-width' as string]: `${sidebarWidth}%` }}
      >
        {/* Main Chat Area - Hidden on mobile when sidebar is open */}
        <div
          className={`flex flex-col bg-bg-primary ${
            sidebarDynamicViewId || sidebarChainId
              ? 'hidden lg:flex'
              : 'flex w-full'
          }`}
          style={{
            width: mainAreaWidth,
            animation: isAnimating
              ? 'chatSatisfyingPush 750ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards'
              : undefined,
            transition:
              proposalIsResizing || isResizing || slideoutIsResizing
                ? 'none'
                : 'width 300ms ease-out',
          }}
        >
          {/* Builder mode gets its own header: the back link goes to the list
              of whatever is being built (automations, validation rules) rather
              than to Chat, and it is visible at every width — the generic one
              below hides its back button at `lg`, which stranded desktop
              builder users, where the builder is most used. */}
          {isBuilderConversationView ? (
            // Shown at EVERY width. On mobile the chat is the full-screen
            // primary surface and the record panel opens over it on demand, so
            // this header is the page header — and the only way out of the
            // builder.
            <BuilderChatHeader
              orgId={orgId}
              entityTypeName={builderSubjectEntityTypeName}
              entityId={builderSubjectId}
              lookupOrgId={builderOrgId}
              title={currentConversation?.title}
            />
          ) : (
            <div className='bg-bg-primary border-b border-border-secondary'>
              <div className='flex items-center justify-between px-3 sm:px-6 py-4'>
                {/* Left side - Back button (mobile) + Breadcrumb */}
                <div className='flex items-center gap-2'>
                  {/* Mobile back button */}
                  <Button
                    color='tertiary'
                    onClick={handleNavigateBack}
                    aria-label='Back to conversations'
                    className='lg:hidden !p-1.5 -ml-1.5'
                    iconLeading={
                      <ArrowLeft
                        className='size-5 text-tertiary'
                        strokeWidth={2}
                      />
                    }
                  />
                  <p className='text-base leading-5 text-black dark:text-white'>
                    <span className='font-normal hidden sm:inline text-neutral-600 dark:text-neutral-400'>
                      Chat /{' '}
                    </span>
                    <span className='font-medium'>
                      <TypewriterTitle title={currentConversation?.title} />
                    </span>
                  </p>
                </div>

                {/* Right side - Materials and Share buttons.
                  Both are suppressed in builder mode. Materials lists the
                  conversation's proposals and dynamic views, which is the
                  surface the panel replaces — offering it would put the same
                  proposal in two places and split the approve action, the same
                  reason the inline proposal card is suppressed. Share offers a
                  conversation whose whole point is a record that may not exist
                  yet. */}
                <div className='flex items-center gap-3'>
                  {/* Desktop: Show both buttons */}
                  {!isMobile && !isBuilderConversationView && (
                    <>
                      <Button
                        color={
                          activeTab === 'materials' ? 'primary' : 'secondary'
                        }
                        size='sm'
                        onClick={handleToggleMaterialsTab}
                      >
                        {activeTab === 'materials'
                          ? 'Conversation'
                          : 'Materials'}
                      </Button>
                      <Button
                        color='secondary'
                        size='sm'
                        iconLeading={Share01}
                        onClick={handleOpenShareModal}
                      >
                        Share
                      </Button>
                    </>
                  )}

                  {/* Mobile: three-dot menu with Materials and Share. The menu
                    holds nothing else, so in builder mode it goes entirely
                    rather than opening empty. */}
                  {isMobile && !isBuilderConversationView && (
                    <div className='relative'>
                      <Button
                        color='secondary'
                        size='sm'
                        onClick={handleToggleMobileMenu}
                        iconLeading={DotsVertical}
                      />

                      {showMobileMenu && (
                        <>
                          {/* Backdrop to close menu */}
                          <div
                            className='fixed inset-0 z-10'
                            onClick={handleCloseMobileMenuBackdrop}
                          />

                          {/* Menu dropdown */}
                          <div className='absolute right-0 mt-2 w-48 bg-bg-primary rounded-lg shadow-lg border border-border-secondary py-1 z-20'>
                            <Button
                              color='tertiary'
                              onClick={handleMobileMaterialsToggleAndCloseMenu}
                              className='w-full !justify-start !px-4 !py-2 !text-sm !font-normal text-fg-primary !rounded-none'
                              noTextPadding
                            >
                              {activeTab === 'materials'
                                ? 'Conversation'
                                : 'Materials'}
                            </Button>
                            <Button
                              color='tertiary'
                              onClick={handleMobileOpenShareModalAndCloseMenu}
                              className='w-full !justify-start !px-4 !py-2 !text-sm !font-normal text-fg-primary !rounded-none'
                              iconLeading={
                                <Share01 className='size-4' strokeWidth={2} />
                              }
                              noTextPadding
                            >
                              Share
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Content Area - Scrollable.
              No bottom padding: the composer is a flex sibling below this, not
              an overlay, so the thread already stops above it. The `pb-32` that
              used to be here was clearance sized for the hero box's wave
              footer and mb-14 overhang, and it read as a large dead gap under
              the last message. */}
          <div
            className='flex-1 min-w-0 overflow-y-auto overflow-x-hidden relative'
            ref={chatContainerRef}
            onScroll={handleScroll}
            tabIndex={-1}
            data-testid='chat-container'
            data-component='chat-container'
          >
            <TableScrollProvider scrollContainerRef={chatContainerRef}>
              {(() => {
                if (activeTab === 'conversation') {
                  return (
                    <InlineEditProvider>
                      <div className='max-w-3xl w-full mx-auto space-y-6 pl-[27px] pr-[17px] sm:pl-[31px] sm:pr-[21px] lg:pl-[39px] lg:pr-[29px] pt-2.5 min-w-0 overflow-x-clip'>
                        {/* Optimistic User Message + Immediate Loading - shown immediately when navigating from conversations home */}
                        {optimisticMessage && chains.length === 0 && (
                          <div className='space-y-4'>
                            {/* User message bubble */}
                            <div className='flex justify-end mb-4'>
                              <div className='max-w-[85vw] sm:max-w-[500px]'>
                                <div className='flex items-center gap-2 justify-end mb-1.5'>
                                  <p className='text-xs sm:text-sm font-medium text-secondary'>
                                    You
                                  </p>
                                </div>
                                <div className='bg-bg-primary border border-border-secondary rounded-bl-[8px] rounded-br-[8px] rounded-tl-[8px] px-3 py-2'>
                                  <p className='text-sm sm:text-base font-normal text-fg-primary leading-6 whitespace-pre-wrap break-words'>
                                    <MessageContent
                                      text={optimisticMessage}
                                      orgId={parseInt(orgId)}
                                    />
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* AI Response - Loading/Thinking indicator shown immediately */}
                            <div className='flex gap-2 sm:gap-3 items-start mb-6'>
                              <div className='flex-1 min-w-0 space-y-1.5'>
                                <ThinkingIndicator
                                  progressStatus={progressStatus}
                                  partialSteps={partialSteps}
                                  onOpenDynamicViewSidebar={
                                    handleOpenDynamicViewSidebar
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        {chains.map((chain, index) => {
                          const steps = chain.steps || [];
                          const finalStep = steps.find(step => step.is_final);
                          const isLastChain = index === chains.length - 1;
                          const isRegenerating =
                            regeneratingChainId === chain.id;
                          // Show thinking if chain is active/processing OR if we're polling and no final step OR if regenerating
                          const showThinking =
                            isRegenerating ||
                            (!finalStep &&
                              (chain.status === 'active' ||
                                (isLastChain && isPolling)));

                          // Detection: completed chain with steps but no final answer = hit max steps
                          // Also check we're not in a race condition during polling -> completed transition
                          const hitMaxSteps =
                            chain.status === 'completed' &&
                            !finalStep &&
                            steps.length > 0 &&
                            !(isLastChain && isPolling);

                          // Collect all dynamic views from all steps in this chain
                          const allChainDynamicViews = steps.flatMap(
                            step => step.dynamic_views || []
                          );

                          // Collect all proposals created in this chain (deduplicate by id)
                          const allChainProposals = Array.from(
                            new Map(
                              steps
                                .flatMap(step => step.created_proposals || [])
                                .map(p => [p.id, p])
                            ).values()
                          );

                          // Last report-shaped tool result across all steps in
                          // this chain. Walk back to find the latest manifest
                          // emitted by query_report / execute_saved_report so
                          // the chart renders inline above the agent's reply.
                          // Per "Last one only" decision: a chain that runs the
                          // tool multiple times only shows the most recent —
                          // and if that most-recent call has no manifest, we
                          // render nothing rather than falling back to an
                          // older manifest from a different question.
                          let lastReportManifest: Record<
                            string,
                            unknown
                          > | null = null;
                          let foundLatestReportTool = false;
                          for (
                            let i = steps.length - 1;
                            i >= 0 && !foundLatestReportTool;
                            i--
                          ) {
                            const stepTools = steps[i].tool_results ?? [];
                            for (let j = stepTools.length - 1; j >= 0; j--) {
                              const tr = stepTools[j];
                              if (
                                tr.tool_name !== 'query_report' &&
                                tr.tool_name !== 'execute_saved_report'
                              ) {
                                continue;
                              }
                              // We found the latest report tool call. Stop
                              // searching after this one — its manifest (or
                              // lack thereof) is authoritative.
                              foundLatestReportTool = true;
                              const { result } = tr;
                              if (
                                result &&
                                typeof result === 'object' &&
                                'manifest' in result
                              ) {
                                const { manifest } = result as {
                                  manifest: unknown;
                                };
                                if (manifest && typeof manifest === 'object') {
                                  lastReportManifest = manifest as Record<
                                    string,
                                    unknown
                                  >;
                                }
                              }
                              break;
                            }
                          }

                          return (
                            <div key={chain.id} className='space-y-4'>
                              {/* User Message - Figma Style */}
                              <div
                                ref={isLastChain ? lastUserMessageRef : null}
                                className='flex justify-end mb-4'
                              >
                                <div className='max-w-[85vw] sm:max-w-[500px]'>
                                  <div className='flex items-center gap-2 justify-end mb-1.5'>
                                    <p className='text-xs sm:text-sm font-medium text-secondary'>
                                      You
                                    </p>
                                  </div>
                                  <div className='bg-bg-primary border border-border-secondary rounded-bl-[8px] rounded-br-[8px] rounded-tl-[8px] px-3 py-2'>
                                    <p className='text-sm sm:text-base font-normal text-fg-primary leading-6 whitespace-pre-wrap break-words'>
                                      <MessageContent
                                        text={chain.user_message}
                                        orgId={parseInt(orgId)}
                                      />
                                    </p>

                                    {/* Display attached files */}
                                    {chain.files && chain.files.length > 0 && (
                                      <div className='mt-4 pt-3 border-t border-border-secondary/50'>
                                        <div className='flex items-center gap-1.5 mb-2'>
                                          <svg
                                            className='size-4 text-tertiary'
                                            fill='none'
                                            viewBox='0 0 24 24'
                                            stroke='currentColor'
                                            strokeWidth={2}
                                          >
                                            <path
                                              strokeLinecap='round'
                                              strokeLinejoin='round'
                                              d='M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13'
                                            />
                                          </svg>
                                          <span className='text-xs font-medium text-tertiary'>
                                            {chain.files.length}{' '}
                                            {chain.files.length === 1
                                              ? 'file'
                                              : 'files'}{' '}
                                            attached
                                          </span>
                                        </div>
                                        <div className='flex flex-wrap gap-2'>
                                          {chain.files.map(
                                            (file: UploadedFile) => (
                                              <InlineFileCard
                                                key={file.id}
                                                file={file}
                                              />
                                            )
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* AI Response - Figma Style.

                                  `min-w-0` on BOTH levels, and it is load-
                                  bearing: a flex child defaults to
                                  `min-width:auto`, so it refuses to shrink
                                  below its content's intrinsic width. Anything
                                  wide inside the agent's message — a code
                                  block, a table, a proposal card — therefore
                                  widened this row past the viewport and took
                                  the prose with it, cutting the agent's own
                                  sentences off mid-word on a phone. `max-w-full`
                                  is the belt to that braces. */}
                              <div className='flex gap-2 sm:gap-3 items-start mb-6 min-w-0 max-w-full'>
                                <div className='flex-1 min-w-0 max-w-full space-y-3'>
                                  {/* Final Answer - Always Visible */}
                                  {(() => {
                                    if (finalStep) {
                                      return (
                                        <div
                                          className='space-y-3'
                                          data-component='chat-final-answer'
                                        >
                                          {/* Inline chart for query_report /
                                              execute_saved_report results.
                                              Sits ABOVE the agent's prose so
                                              users see the visualisation
                                              first without opening the
                                              Reasoning side panel. */}
                                          {lastReportManifest && (
                                            <InlineReportChart
                                              manifest={
                                                lastReportManifest as unknown as ViewDefinition
                                              }
                                            />
                                          )}
                                          <div className='text-base font-normal text-fg-primary leading-7'>
                                            <MarkdownContent
                                              content={preprocessEntityTags(
                                                finalStep.content
                                              )}
                                              remarkPlugins={[remarkBreaks]}
                                              rehypePlugins={[
                                                rehypeRaw,
                                                [
                                                  rehypeSanitize,
                                                  sanitizeSchema,
                                                ],
                                              ]}
                                              components={markdownComponents}
                                            />
                                          </div>

                                          {/* Step limit warning */}
                                          {chain.step_limit_reached && (
                                            <StepLimitWarningBanner className='mt-3' />
                                          )}

                                          {/* Proposals render inline when the
                                              `inline_proposals` flag is
                                              enabled, or when this is a
                                              builder conversation regardless
                                              of the flag — builder cards are
                                              selectors the side panel depends
                                              on, so they must always exist.
                                              Otherwise proposals fall back to
                                              `ChainResultsPreview` sidebar
                                              cards.

                                              In a builder conversation the
                                              card's action is SELECT, not
                                              approve/reject: the panel beside
                                              the chat carries the Save that
                                              applies a change, so a card
                                              offering its own approve/reject
                                              would split that single apply
                                              path across two places. Selecting
                                              a card only changes which
                                              proposal's draft the panel shows. */}
                                          {showInline
                                            ? allChainProposals.map(
                                                proposal => (
                                                  <InlineProposal
                                                    key={proposal.id}
                                                    proposalId={proposal.id}
                                                    orgId={parseInt(orgId)}
                                                    title={proposal.title}
                                                    resolution={
                                                      proposal.resolution
                                                    }
                                                    isFresh={
                                                      isLastChain &&
                                                      !!currentTaskId
                                                    }
                                                    actionCount={
                                                      proposal.action_count
                                                    }
                                                    isBuilderMode={
                                                      isBuilderConversationView
                                                    }
                                                    // "Showing in panel" means
                                                    // exactly that: this card's
                                                    // draft is the one rendered.
                                                    // Builder: read off the
                                                    // draft rather than
                                                    // re-deriving the pick, so
                                                    // a resolved newest (no
                                                    // draft) or a stale click
                                                    // never badges a card the
                                                    // panel is not showing.
                                                    // Ordinary chat: the open
                                                    // (docked) proposal is the
                                                    // one showing.
                                                    isSelected={
                                                      isBuilderConversationView
                                                        ? proposal.id ===
                                                          builderDraft?.proposalId
                                                        : proposal.id ===
                                                          openProposalId
                                                    }
                                                    onSelect={
                                                      isBuilderConversationView
                                                        ? handleSelectProposal
                                                        : undefined
                                                    }
                                                  />
                                                )
                                              )
                                            : allChainProposals.length > 0 && (
                                                <ChainResultsPreview
                                                  proposals={allChainProposals}
                                                  dynamicViews={[]}
                                                />
                                              )}

                                          {/* Dynamic Views Preview */}
                                          {allChainDynamicViews.length > 0 && (
                                            <ChainResultsPreview
                                              proposals={[]}
                                              dynamicViews={
                                                allChainDynamicViews
                                              }
                                              onOpenDynamicViewSidebar={
                                                handleOpenDynamicViewSidebar
                                              }
                                            />
                                          )}

                                          {/* Show Reasoning Button and Action Icons Row */}
                                          <div className='flex items-center justify-between pt-1'>
                                            {/* Show Reasoning Button - Left Side */}
                                            {steps.length > 0 && (
                                              <Button
                                                color='gray-soft'
                                                onClick={() =>
                                                  handleOpenReasoningSidebar(
                                                    chain.id
                                                  )
                                                }
                                                className='!rounded-full !py-1 !font-medium'
                                              >
                                                Show reasoning
                                              </Button>
                                            )}

                                            {/* Action Icons - Right Side */}
                                            <div className='flex items-center gap-1.5 ml-auto'>
                                              {/* Refresh button - only on last completed chain */}
                                              {/* TODO(copy): Laura sign-off — retry framing for AI content (no "regenerate"). */}
                                              {isLastChain &&
                                                chain.status ===
                                                  'completed' && (
                                                  <Button
                                                    color='tertiary'
                                                    onClick={() =>
                                                      handleRetry(chain.id)
                                                    }
                                                    isDisabled={
                                                      !!regeneratingChainId
                                                    }
                                                    title='Try again'
                                                    aria-label='Try again'
                                                    className='!p-1.5 !rounded'
                                                    iconLeading={
                                                      <RefreshCw01
                                                        className={`size-4 stroke-tertiary ${regeneratingChainId === chain.id ? 'animate-spin' : ''}`}
                                                        strokeWidth={2}
                                                      />
                                                    }
                                                  />
                                                )}
                                              <Button
                                                color='tertiary'
                                                onClick={() =>
                                                  handleCopy(
                                                    chain.id,
                                                    finalStep?.content
                                                  )
                                                }
                                                title={
                                                  copiedChainId === chain.id
                                                    ? 'Copied'
                                                    : 'Copy'
                                                }
                                                className={`!p-1.5 !rounded ${copiedChainId === chain.id ? 'bg-bg-secondary' : ''}`}
                                                iconLeading={
                                                  copiedChainId === chain.id ? (
                                                    <Check
                                                      className='size-4 stroke-utility-success-600'
                                                      strokeWidth={2}
                                                    />
                                                  ) : (
                                                    <Copy01
                                                      className='size-4 stroke-tertiary'
                                                      strokeWidth={2}
                                                    />
                                                  )
                                                }
                                              />
                                              <Button
                                                color='tertiary'
                                                onClick={() =>
                                                  handleFeedback(
                                                    chain.id,
                                                    'positive'
                                                  )
                                                }
                                                title='Thumbs up'
                                                className={`!p-1.5 !rounded ${chainFeedback[chain.id] === 'positive' ? 'bg-bg-secondary' : ''}`}
                                                iconLeading={
                                                  <ThumbsUp
                                                    className={`size-4 ${chainFeedback[chain.id] === 'positive' ? 'stroke-utility-success-600 fill-utility-success-100' : 'stroke-tertiary'}`}
                                                    strokeWidth={2}
                                                  />
                                                }
                                              />
                                              <Button
                                                color='tertiary'
                                                onClick={() =>
                                                  handleFeedback(
                                                    chain.id,
                                                    'negative'
                                                  )
                                                }
                                                title='Thumbs down'
                                                className={`!p-1.5 !rounded ${chainFeedback[chain.id] === 'negative' ? 'bg-bg-secondary' : ''}`}
                                                iconLeading={
                                                  <ThumbsDown
                                                    className={`size-4 ${chainFeedback[chain.id] === 'negative' ? 'stroke-utility-error-600 fill-utility-error-100' : 'stroke-tertiary'}`}
                                                    strokeWidth={2}
                                                  />
                                                }
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    } else if (showThinking) {
                                      return (
                                        <ThinkingIndicator
                                          progressStatus={progressStatus}
                                          partialSteps={partialSteps}
                                          chainId={chain.id}
                                          onOpenDynamicViewSidebar={
                                            handleOpenDynamicViewSidebar
                                          }
                                        />
                                      );
                                    } else if (hitMaxSteps) {
                                      return (
                                        <div className='space-y-2'>
                                          {/* Warning message for max steps reached */}
                                          <div className='bg-utility-warning-50 border border-utility-warning-200 rounded-xl px-4 py-3'>
                                            <div className='flex items-center gap-3'>
                                              <svg
                                                className='w-5 h-5 text-utility-warning-600 flex-shrink-0'
                                                fill='none'
                                                stroke='currentColor'
                                                viewBox='0 0 24 24'
                                              >
                                                <path
                                                  strokeLinecap='round'
                                                  strokeLinejoin='round'
                                                  strokeWidth={2}
                                                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
                                                />
                                              </svg>
                                              <span className='text-sm text-utility-warning-700 font-medium'>
                                                This request was too complex to
                                                complete. Try again, or break it
                                                into smaller parts.
                                              </span>
                                            </div>
                                          </div>

                                          {/* Action buttons row */}
                                          <div className='flex items-center justify-between pt-1'>
                                            {/* Show Reasoning Button - Left Side */}
                                            <Button
                                              color='gray-soft'
                                              onClick={() =>
                                                handleOpenReasoningSidebar(
                                                  chain.id
                                                )
                                              }
                                              className='!rounded-full !py-1 !font-medium'
                                            >
                                              Show reasoning
                                            </Button>

                                            {/* Try Again Button - Right Side */}
                                            {isLastChain && (
                                              <Button
                                                color='primary'
                                                onClick={() =>
                                                  handleRetry(chain.id)
                                                }
                                                isDisabled={
                                                  !!regeneratingChainId
                                                }
                                                aria-label='Try again'
                                                className='!bg-utility-warning-600 hover:!bg-utility-warning-700 !shadow-none !ring-0 before:!border-transparent'
                                                iconLeading={
                                                  <RefreshCw01
                                                    className={`size-4 ${regeneratingChainId === chain.id ? 'animate-spin' : ''}`}
                                                    strokeWidth={2}
                                                  />
                                                }
                                              >
                                                Try again
                                              </Button>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    } else if (chain.budget_exceeded) {
                                      return <ReasoningLimitBanner />;
                                    } else if (chain.status === 'failed') {
                                      return (
                                        <div className='bg-utility-error-50 border border-utility-error-200 rounded-xl px-4 py-3'>
                                          <div className='flex items-center justify-between gap-3'>
                                            <div className='flex items-center gap-3'>
                                              <svg
                                                className='w-5 h-5 text-utility-error-600 flex-shrink-0'
                                                fill='none'
                                                stroke='currentColor'
                                                viewBox='0 0 24 24'
                                              >
                                                <path
                                                  strokeLinecap='round'
                                                  strokeLinejoin='round'
                                                  strokeWidth={2}
                                                  d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                                                />
                                              </svg>
                                              <span className='text-sm text-utility-error-700 font-medium'>
                                                We couldn't generate a response.
                                                Try again.
                                              </span>
                                            </div>
                                            {/* Retry button - only on last failed chain */}
                                            {isLastChain && (
                                              <Button
                                                color='primary-destructive'
                                                onClick={() =>
                                                  handleRetry(chain.id)
                                                }
                                                isDisabled={
                                                  !!regeneratingChainId
                                                }
                                                aria-label='Retry'
                                                iconLeading={
                                                  <RefreshCw01
                                                    className={`size-4 ${regeneratingChainId === chain.id ? 'animate-spin' : ''}`}
                                                    strokeWidth={2}
                                                  />
                                                }
                                              >
                                                Retry
                                              </Button>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    } else {
                                      return null;
                                    }
                                  })()}
                                </div>
                              </div>
                            </div>
                          );
                        })}

                        {/* Loading state when chains is empty but we're polling/loading (and no optimistic message already showing) */}
                        {chains.length === 0 &&
                          !optimisticMessage &&
                          (isPolling || currentTaskId || loading) && (
                            <ThinkingIndicator
                              progressStatus={progressStatus}
                              partialSteps={partialSteps}
                              onOpenDynamicViewSidebar={
                                handleOpenDynamicViewSidebar
                              }
                            />
                          )}
                      </div>
                    </InlineEditProvider>
                  );
                } else if (activeTab === 'materials') {
                  return (
                    /* Materials Tab Content (Proposals + Dynamic Views) */
                    <div className='max-w-4xl mx-auto px-4'>
                      <MaterialsList
                        conversationId={
                          currentConversation?.id
                            ? parseInt(currentConversation.id)
                            : 0
                        }
                        orgId={orgId || ''}
                        onOpenDynamicViewSidebar={handleOpenDynamicViewSidebar}
                        refetchKey={proposalUpdateCounter}
                      />
                    </div>
                  );
                } else {
                  return null;
                }
              })()}
            </TableScrollProvider>
          </div>

          {/* New Messages Button - fixed above input area */}
          {hasNewContent && (
            <div className='flex justify-center pb-2'>
              <Button
                onClick={handleNewMessagesClick}
                className='flex items-center gap-1.5 px-3 py-1.5 rounded-full shadow-lg'
                size='sm'
                aria-label='Scroll to new messages'
              >
                New messages ↓
              </Button>
            </div>
          )}

          {/* Pending-draft CTA. Mobile only — on desktop the panel is already
              on screen, so there is nothing to summon. Without this a draft
              can land while the user is mid-sentence and go unnoticed, because
              the panel is not visible. */}
          {isMobileBuilderView && (
            <BuilderReviewBar
              // The same number the slideout's SaveBar shows — both count the
              // draft's changed properties, so the bar can never promise a
              // different amount of work than the panel it opens. It was
              // `builderDraft ? 1 : 0`, a boolean wearing a count's clothes,
              // which read "1 change" over a proposal carrying several.
              pendingCount={builderPendingChangeCount}
              // Once a record exists there is always something worth opening,
              // draft or not — the edit path starts in that state.
              hasRecord={Boolean(builderSubjectId)}
              recordLabel={builderRecordLabel}
              onReview={openMobilePanel}
            />
          )}

          {/* Follow-up Chat Input.

              COMPACT below `lg` (useCompactComposer = !isLargeScreen), builder
              mode included — the same ConversationInput the proposal side
              panel and email composer use, so there is one chat input in the
              product rather than a hero one and a panel one that drift.

              The hero box stays at `lg` and above, builder mode included —
              desktop always keeps the full-width conversation surface it was
              designed for. Its wave footer and mb-14 overhang are exactly
              what make it wrong in a narrow column or on a phone. */}
          {useCompactComposer ? (
            <ConversationInput
              value={followUpMessage}
              onChange={setFollowUpMessage}
              onSubmit={continueConversation}
              placeholder={followUpPlaceholder}
              loading={loading}
              error={error}
              autoFocus
              orgId={orgId ? parseInt(orgId, 10) : undefined}
              uploadedFiles={uploadedFiles}
              onFilesChange={setUploadedFiles}
              // Explicit: ConversationInput defaults enableVoice to
              // `variant !== 'simple'`, so omitting it removes voice input.
              enableVoice
              variant='simple'
              sticky={false}
              compactUntilWrap
              // Inset from the edges. The hero composer carries its own
              // generous surrounding space; the simple variant has none, so
              // without this it sits flush against the viewport on a phone and
              // reads like part of the chrome rather than a control.
              className='mx-3 mb-3'
            />
          ) : (
            <HeroChatInput
              value={followUpMessage}
              onChange={setFollowUpMessage}
              onSubmit={continueConversation}
              placeholder={followUpPlaceholder}
              loading={loading}
              error={error}
              autoFocus
              orgId={orgId ? parseInt(orgId, 10) : undefined}
              uploadedFiles={uploadedFiles}
              onFilesChange={setUploadedFiles}
            />
          )}

          {/* Disclaimer text. Suppressed in builder mode: the panel beside the
              chat is the surface the user is judging, and a centred caption
              under a narrow column reads as chrome rather than a caveat.

              The negative top margin exists to close the gap under the HERO
              box; applied under the compact input it pulls the text into the
              field. So it is hero-only, while the caveat itself stays. */}
          {!isBuilderConversationView && (
            <div
              className={cx(
                'max-w-3xl mx-auto px-3 sm:px-4 lg:px-6',
                !useCompactComposer && '-mt-[21px] sm:-mt-[13px]'
              )}
            >
              <p className='text-xs text-fg-quaternary text-center pt-[4px] pb-[7px]'>
                AI can make mistakes. Always double-check responses.
              </p>
            </div>
          )}
        </div>

        {/* Sidebar Column (DynamicViews or Reasoning) - Full-screen on mobile, sidebar on desktop */}
        {(sidebarDynamicViewId || sidebarChainId) && currentConversation && (
          <div
            className='fixed lg:relative inset-0 lg:inset-auto z-50 lg:z-auto bg-bg-primary lg:bg-bg-primary/90 lg:backdrop-blur-sm overflow-y-hidden overflow-x-visible lg:flex-shrink-0 p-0 m-0 shadow-lg w-full lg:[width:var(--sidebar-width)]'
            style={{
              ['--sidebar-width' as string]: isAnimating
                ? '0'
                : `${sidebarWidth}%`,
              marginRight: 0,
              paddingRight: 0,
              animation: isAnimating
                ? 'sidebarSmoothSlide 750ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards'
                : undefined,
              transition:
                isResizing || isAnimating ? 'none' : 'width 300ms ease-out',
            }}
          >
            {/* Content Area with Premium Styling */}
            <div className='border-l border-border-secondary h-full relative'>
              {/* Resize handle on the chat-facing edge — the same invisible
                  edge strip the slideouts use. Desktop only (the panel is a
                  full-screen modal on mobile) and never mid-entrance. */}
              {!isAnimating && (
                <div className='max-lg:hidden'>
                  <EdgeResizeHandle
                    edge='left'
                    onResizeStart={handleResizeStart}
                    onKeyboardResize={resizeSidebarBy}
                    width={Math.round(sidebarWidth)}
                    minWidth={SIDEBAR_MIN_WIDTH_PERCENT}
                    maxWidth={SIDEBAR_MAX_WIDTH_PERCENT}
                    isResizing={isResizing}
                    aria-label='Resize sidebar panel. Use left and right arrow keys to resize.'
                  />
                </div>
              )}
              {/* Conditional Sidebar Content */}
              {sidebarDynamicViewId && (
                <DynamicViewSidebar
                  conversationId={parseInt(currentConversation.id)}
                  orgId={orgId || ''}
                  viewId={sidebarDynamicViewId}
                  onClose={handleCloseSidebar}
                  isCreator={
                    currentConversation?.created_by === userProfile?.id
                  }
                />
              )}
              {sidebarChainId && (
                <ReasoningSidebar
                  chain={chains.find(c => c.id === sidebarChainId) || null}
                  onClose={handleCloseSidebar}
                  onOpenDynamicViewSidebar={handleOpenDynamicViewSidebar}
                />
              )}
            </div>
          </div>
        )}

        {/* Docked proposal pane — a real flex sibling, like the builder
            record panel below (and subject to the same "must be inside this
            flex row" constraint). Rendered only while this page holds the
            inline-host claim AND a proposal tops the slideout stack; every
            other case falls back to the global ProposalSidebar overlay. */}
        {dockedProposalEntry && (
          <InlineProposalPanel
            entry={dockedProposalEntry}
            width={slideoutWidth}
            onApproved={reloadConversationAfterResolution}
            onRejected={reloadConversationAfterResolution}
            resizeHandle={
              <EdgeResizeHandle
                edge='left'
                onResizeStart={handleProposalPaneResizeStart}
                onKeyboardResize={resizeProposalPaneBy}
                width={slideoutWidth}
                minWidth={INLINE_PANEL_MIN_WIDTH}
                maxWidth={INLINE_PANEL_MAX_WIDTH}
                isResizing={slideoutIsResizing}
                aria-label='Resize proposal panel. Use left and right arrow keys to resize.'
              />
            }
          />
        )}

        {/* The object being built. This MUST be inside the flex row above —
            the element wrapping that row is `display: block`, so a sibling
            placed after it stacks BELOW the full-height chat column, lands at
            y=viewportHeight, and is clipped away by `overflow-hidden`. The
            chat column has already given up `slideoutWidth` for this panel, so
            the symptom is an empty strip beside the chat and a panel nobody
            can see — present in the DOM, with the right content, entirely
            off-screen. Do not move this out of the flex row. */}
        <BuilderRecordPanelDesktop
          visible={builderPanelVisible}
          slideoutWidth={slideoutWidth}
          orgId={builderOrgId}
          entityTypeName={builderSubjectEntityTypeName}
          entityId={builderSubjectId}
          overlays={builderOverlays}
          draft={builderDraft}
          onApproved={reloadConversationAfterResolution}
          onRejected={reloadConversationAfterResolution}
        />
      </div>

      {/* Mobile builder: the record panel, full screen over the chat.
          Rendered as a sibling of the chat column rather than inside it so it
          covers the header too — the panel is its own surface while open, with
          its own way back (onClosePanel), not a section of the conversation. */}
      <BuilderRecordPanelMobileOverlay
        visible={isMobileBuilderView && isMobilePanelOpen}
        orgId={builderOrgId}
        entityTypeName={builderSubjectEntityTypeName}
        entityId={builderSubjectId}
        overlays={builderOverlays}
        draft={builderDraft}
        onApproved={reloadConversationAfterResolution}
        onRejected={reloadConversationAfterResolution}
        onClosePanel={closeMobilePanel}
      />

      <ConversationModals
        orgId={orgId}
        conversationId={currentConversation?.id}
        conversationTitle={currentConversation?.title}
        isShareModalOpen={isShareModalOpen}
        onCloseShareModal={handleCloseShareModal}
        feedbackModalChainId={feedbackModalChainId}
        feedbackModalOpen={feedbackModalOpen}
        feedbackModalSteps={feedbackModalSteps}
        onFeedbackModalClose={handleFeedbackModalClose}
        onFeedbackSubmit={handleStructuredFeedback}
      />
    </div>
  );
}
